import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, AlertCircle } from 'lucide-react';
import FallbackImage from '../components/FallbackImage';

const API = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001') + '/api';

const ProgrammingProductDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  
  // Decode the product ID from URL
  const decodedId = decodeURIComponent(id);

  useEffect(() => {
    fetchProduct();
  }, [decodedId]);

  useEffect(() => {
    if (product && product.colors && product.colors.length > 0) {
      setSelectedColor(product.colors[0]);
      if (product.colors[0].sizes.length > 0) {
        setSelectedSize(product.colors[0].sizes[0]);
      }
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/programming/products/${encodeURIComponent(decodedId)}`, { headers });
      setProduct(response.data);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Error al cargar producto');
    } finally {
      setLoading(false);
    }
  };

  const formatServiceDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const addToCart = () => {
    if (!user) {
      toast.error('Debes iniciar sesión para hacer pedidos');
      navigate('/login');
      return;
    }

    if (user.role !== 'admin' && !user.approved) {
      toast.error('Tu cuenta debe ser aprobada para hacer pedidos de programación');
      return;
    }

    if (!selectedSize) {
      toast.error('Selecciona una talla');
      return;
    }

    // Generate unique variant_id using EAN (unique per size/color)
    const uniqueVariantId = selectedSize.ean || `${product.id}-${selectedColor.color}-${selectedSize.size}`;
    
    const cartItem = {
      variant_id: uniqueVariantId,
      reference: selectedSize.reference,
      ean: selectedSize.ean,
      model_name: product.model_name,
      color: selectedColor.color,
      size: selectedSize.size,
      quantity: quantity,
      price: (user && user.approved && selectedSize.pt_price) ? selectedSize.pt_price : selectedSize.pvpr_price,
      image: product.images[0] || '',
      is_programming: true,
      season: product.season,
      service_date: product.service_date
    };

    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingIndex = cart.findIndex(
      item => item.variant_id === cartItem.variant_id && item.is_programming
    );

    if (existingIndex !== -1) {
      cart[existingIndex].quantity += quantity;
      toast.success(`Cantidad actualizada en el carrito`);
    } else {
      cart.push(cartItem);
      toast.success(`${cartItem.model_name} añadido al carrito de programación`);
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdate'));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600 text-lg">Cargando...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600 text-lg">Producto no encontrado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <Link to="/programming" className="inline-flex items-center text-white hover:text-gray-300">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver a Programación SS26
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Programming Banner */}
        <div className="bg-blue-600 text-white rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white text-blue-600 px-3 py-1 rounded-full font-bold">
              {product.season}
            </div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              <span className="font-semibold">
                Disponible desde: {formatServiceDate(product.service_date)}
              </span>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl mb-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-amber-900 font-semibold">Producto de Programación - Sin Stock Actual</p>
              <p className="text-amber-800 text-sm mt-1">
                Este pedido se servirá a partir de la fecha indicada. No es un producto con disponibilidad inmediata.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Images */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <FallbackImage
                images={product.images}
                alt={product.model_name}
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {product.model_name}
              </h1>
              <p className="text-lg text-gray-600">{product.color}</p>
            </div>

            {product.description && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">{product.description}</p>
              </div>
            )}

            {/* Color Selection */}
            {selectedColor && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Color</h3>
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3">
                  <p className="font-medium text-gray-900">{selectedColor.color}</p>
                </div>
              </div>
            )}

            {/* Size Selection */}
            {selectedColor && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Talla</h3>
                <div className="grid grid-cols-4 gap-2">
                  {selectedColor.sizes.map((size) => (
                    <button
                      key={size.ean}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 rounded-lg border-2 transition ${
                        selectedSize?.ean === size.ean
                          ? 'border-blue-600 bg-blue-50 text-blue-600 font-semibold'
                          : 'border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {size.size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Cantidad</h3>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 px-4 py-2 border-2 border-gray-300 rounded-lg"
              />
            </div>

            {/* Price */}
            <div className="bg-blue-50 rounded-xl p-4">
              {selectedSize ? (
                <div>
                  {user && (user.role === 'admin' || user.approved) && selectedSize.pt_price ? (
                    <>
                      <p className="text-3xl font-bold text-blue-600">
                        €{selectedSize.pt_price.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600">Precio tarifa (PT)</p>
                      <p className="text-sm text-gray-500 mt-1">
                        PVPR: €{selectedSize.pvpr_price.toFixed(2)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-3xl font-bold text-black">
                        €{selectedSize.pvpr_price.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600">Precio público (PVPR)</p>
                      {user && user.role !== 'admin' && !user.approved && (
                        <p className="text-xs text-yellow-700 mt-2 bg-yellow-100 p-2 rounded">
                          ⏳ Cuenta pendiente de aprobación
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <p className="text-gray-600">Selecciona una talla para ver el precio</p>
              )}
            </div>

            {/* Add to Cart Button */}
            {user && user.role !== 'admin' && !user.approved ? (
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 text-center">
                <p className="text-yellow-900 font-semibold mb-2">⏳ Cuenta Pendiente de Aprobación</p>
                <p className="text-sm text-yellow-800">
                  No puedes realizar pedidos de programación hasta que tu cuenta sea aprobada.
                </p>
              </div>
            ) : (
              <Button
                onClick={addToCart}
                disabled={!selectedSize}
                className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg text-white"
              >
                Añadir a Carrito de Programación
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgrammingProductDetail;
